import { IReactiveModel, ViewModel } from '@descomplica/microfrontend-core';
import { IsArray, IsNotEmpty, IsString, Matches } from 'class-validator';
import { action, makeAutoObservable, observable } from 'mobx';
import { toast } from 'react-toastify';

import {
  CreateInvitesCollaboration,
  INVITES_CREATED,
} from '@/modules/recognition/admin/application/collaborations/create-invites.collaboration';
import { GetRolesCollaboration } from '@/modules/recognition/admin/application/collaborations/get-roles.collaboration';
import { GetScopesCollaboration } from '@/modules/recognition/admin/application/collaborations/get-scopes.collaboration';
import type {
  CreateInvitesDto,
  EmailDto,
  RoleDto,
  ScopeDto,
} from '@/shared/application/interfaces/pegasus-io.gateway';
import { ObservableForm } from '@/shared/presentation/forms/BaseForm';

import { Tag } from './components/MultipleEmailInput';

class InviteForm {
  @IsNotEmpty({ message: 'Campo obrigatório' })
  role: RoleDto;

  @IsArray({ message: 'Campo obrigatório' })
  scopes: ScopeDto[] = [];

  @IsArray({ message: 'Campo obrigatório' })
  emails: EmailDto[] = [];

  @IsString({ message: 'Campo obrigatório' })
  @Matches(
    /^([a-z0-9]+(?:-[a-z0-9]+)*[-a-z0-9]*\*?)(?:,\s*[a-z0-9]+(?:-[a-z0-9]+)*[-a-z0-9]*\*?)*$/,
    {
      message:
        'O nome das tags devem conter apenas letras minúsculas, números, hífens e asteriscos(*) no final da tag. Separadas por vírgula. Ex: tag1,tag2,tag3*',
    },
  )
  tags: string;

  reset(): void {
    this.role = null;
    this.scopes = [];
    this.emails = [];
  }
}

@ViewModel({ singleton: true })
export class CreateInvitationViewModel implements IReactiveModel {
  @observable form = new ObservableForm(InviteForm);
  @observable roles: RoleDto[] = [];
  @observable scopes: ScopeDto[] = [];
  @observable emailsPreImported = [];
  @observable emailsImported = [];
  @observable invalidEmails = [];
  @observable isOpen = false;
  @observable isLoading = false;
  @observable hasError = false;
  @observable isSubmitting = false;
  @observable isInvitesFeedbackOpen = false;
  @observable invites = [];

  constructor(
    private getRolesCollaboration: GetRolesCollaboration,
    private getScopesCollaboration: GetScopesCollaboration,
    private createInvitesCollaboration: CreateInvitesCollaboration,
  ) {
    makeAutoObservable(this);
  }

  @action
  async onInit() {
    try {
      this.isLoading = true;

      const [{ data: roles }, { data: scopes }] = await Promise.all([
        this.getRolesCollaboration.run(),
        this.getScopesCollaboration.run(),
      ]);

      this.roles = roles;
      this.scopes = scopes.filter(
        (scope) => scope.guid !== process.env.REACT_APP_RECOGNITION_DESCOMPLICA_IO_SCOPE,
      );
    } catch (error) {
      console.error(error);
      this.hasError = true;
    } finally {
      this.isLoading = false;
    }
  }

  handleTags(tags: string[]): string {
    return tags.map((i) => i.replaceAll(/\s/g, '-')).toString();
  }

  @action
  async create() {
    try {
      this.isSubmitting = true;

      const scopeGuids = this.form.values.scopes.map((scope: Tag) => {
        return scope.guid;
      });

      const tags = scopeGuids
        .map((scope) => {
          return this.form.values.tags.split(',').map((i) => `lms-${scope}-${i.trim()}`);
        })
        .flat();

      const dto: CreateInvitesDto = {
        emails: this.form.values.emails.map((email) => email.name),
        roleGuid: this.form.values.role.guid,
        scopeGuids: scopeGuids,
        tags: this.handleTags(tags),
      };

      const result = await this.createInvitesCollaboration.run(dto);
      if (result?.identifier !== INVITES_CREATED) {
        throw new Error('Erro ao criar convites');
      }

      this.invites = result.data;
      this.isInvitesFeedbackOpen = true;
    } catch (error) {
      console.error(error);
      toast.error('Erro ao criar convites');
    } finally {
      this.isSubmitting = false;
    }
  }

  @action
  setIsOpen(isOpen: boolean) {
    this.isOpen = isOpen;
  }

  @action
  setIsInvitesFeedbackOpen(isOpen: boolean) {
    this.reset();
    this.isInvitesFeedbackOpen = isOpen;
  }

  @action
  reset() {
    this.form.reset();
    this.emailsImported = [];
    this.emailsPreImported = [];
    this.invalidEmails = [];
  }

  @action
  resetImport() {
    this.emailsImported = [];
    this.emailsPreImported = [];
    this.invalidEmails = [];
  }

  @action
  handleInvalidError(invalidEmails: any, validEmails: Tag[]) {
    this.invalidEmails = invalidEmails;
    this.emailsPreImported = validEmails;
    this.setIsOpen(true);
  }

  @action
  importEmails(validEmails: Tag[]) {
    const emailsNotDuplicated = [
      ...new Map(validEmails.map((email) => [email.name, email])).values(),
    ];

    this.form.values.emails = emailsNotDuplicated;
    this.emailsImported = emailsNotDuplicated;
  }
}
